import { apiClient } from "@/api/client";
import { AxiosError } from "axios";
import { create } from "zustand";

interface AuthState {
  user: User | null;
  user_permissions: string[] | null;
  user_role: string | null;
  sidebar: any[] | null;
  authToken: string | null;
  media_agency_information: MediaAgencyInformation | null;
  setUser: (user: User) => void;
  setAuthToken: (token: string) => void;
  setAuth: (
    user: User,
    token: string,
    user_permissions: string[],
    user_role: string,
    sidebar: any[],
    media_agency_information: MediaAgencyInformation
  ) => void;
  logout: () => void;
  getAuth: () => Promise<void>;
  login: (data: LoginRequest) => Promise<LoginResponse>;
}

export interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture?: string;
  language: string;
  timezone: string;
  bio: string;
  user_type?: string;
}

export interface SidebarItem {
  id?: string | null;
  title: string;
  route: string;
  icon?: string | null;
  order?: number | null;
  visible?: boolean | null;
  permission?: string | null;
  children?: SidebarItem[] | null;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponseData {
  user: User;
  token: string;
}

export interface LoginResponse {
  data: LoginResponseData;
  message: string;
  success: boolean;
}

export interface UserResponse {
  user: User;
  roles: string[];
  permissions: string[];
  sidebar: SidebarItem[];
  media_agency_information: MediaAgencyInformation;
}

export interface MediaAgencyInformation {
  id: string;
  name: string;
  logo_url: string;
  logo_url_signed: string;
  bio: string | null;
  website: string;
  industry: string;
  size: string;
  phone: string;
  main_email: string;
  gst_id: string | null;
  agency_terms_accepted: boolean;
  agency_joined_at: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  billing_address: BillingAddress;
  recovery_email?: string;
  recovery_email_verified?: boolean;
  recovery_phone?: string;
  recovery_phone_verified?: boolean;
}

export interface BillingAddress {
  id: number;
  media_agency_information_id: string;
  address_line_1: string;
  address_line_2?: string | null;
  city: string;
  state: string;
  country: string;
  zip_postal: string;
  phone?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  user_permissions: null,
  user_role: null,
  sidebar: null,
  authToken: null,
  media_agency_information: null,
  setUser: (user) => {
    set({ user });
  },
  setAuthToken: (token) => {
    localStorage.setItem("authToken", token);
    set({ authToken: token });
  },
  setAuth: (
    user,
    token,
    user_permissions,
    user_role,
    sidebar,
    media_agency_information
  ) => {
    localStorage.setItem("authToken", token);
    set({
      user,
      authToken: token,
      user_permissions,
      user_role,
      sidebar,
      media_agency_information,
    });
  },

  logout: () => {
    localStorage.removeItem("authToken");
    set({ user: null, authToken: null });
  },

  getAuth: async () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      if (
        get().user &&
        get().user_permissions &&
        get().user_role &&
        get().sidebar
      )
        return;

      try {
        const response = await apiClient.get<UserResponse>("/users/info");
        const data = response.data;
        get().setAuth(
          data.user,
          token,
          data.permissions,
          data.roles[0],
          data.sidebar,
          data.media_agency_information
        );
      } catch (error) {
        const axiosError = error as AxiosError<any>;
        const errorMessage = axiosError.response
          ? axiosError.response.data?.message || "Something went wrong"
          : "Something went wrong";
        get().logout();
        throw new Error(errorMessage);
      }
    } else {
      get().logout();
      throw new Error("Please log in again.");
    }
  },

  login: async (data: LoginRequest) => {
    try {
      const response = await apiClient.post<LoginResponse>("/login", data);
      const { token } = response.data.data;
      get().setAuthToken(token);
      const response1 = await apiClient.get<UserResponse>("/users/info");
      const responseData = response1.data;
      get().setAuth(
        responseData.user,
        token,
        responseData.permissions,
        responseData.roles[0],
        responseData.sidebar,
        responseData.media_agency_information
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },
}));

export default useAuthStore;
